"use client";

import { StorefrontHeader, type StorefrontHeaderProps } from "@wmart/ui";
import { useCart } from "@/app/[locale]/(shop)/cart/CartProvider";
import { LocationChip } from "../src/components/LocationChip";
import { LocaleSwitcher } from "./LocaleSwitcher";

/**
 * Storefront-local adapter that subscribes the header to the live cart state.
 *
 * StorefrontHeader (in @wmart/ui) is intentionally presentational — it takes
 * cartCount/cartTotalMinor as static props (Plan 11 rule L7-UI: shells stay
 * data-agnostic). The shop layout passes server-computed initial values, but
 * mutations from QtyAndAdd / CartDrawer / CartClient only update CartProvider's
 * client state. This wrapper bridges the gap by re-reading useCart() on every
 * render and forwarding the live numbers to the header's existing prop slots.
 *
 * For anon / pending visitors CartProvider holds `cart === null`; in that case
 * we forward no overrides, so the header keeps whatever the server passed
 * (which, per (shop)/layout.tsx, is an empty object for non-approved users —
 * no badge, no total).
 *
 * P13-T12: `isApproved` is forwarded from the server layout so LocationChip
 * can gate itself without re-fetching the session on the client.
 */
interface HeaderWithLiveCartProps extends StorefrontHeaderProps {
  isApproved?: boolean;
}

export function HeaderWithLiveCart({
  isApproved = false,
  ...props
}: HeaderWithLiveCartProps) {
  const { cart, lineCount, openCart } = useCart();
  const live = cart
    ? { cartCount: lineCount, cartTotalMinor: cart.totals.subtotalMinor }
    : {};
  const locationSlot = <LocationChip isApproved={isApproved} />;
  const localeSwitcherSlot = <LocaleSwitcher />;
  return (
    <StorefrontHeader
      {...props}
      {...live}
      onCartClick={openCart}
      locationSlot={locationSlot}
      localeSwitcherSlot={localeSwitcherSlot}
    />
  );
}
